/**
 * S16 — SAH wage-capture derivation (travel time, broken shift, min engagement).
 *
 * Pure, tenant-timezone-aware, idempotent. NO award-engine rate math.
 * Contract: docs/specs/S16_SAH_WAGE_CAPTURE_CONTRACT.md
 *
 * Ordering (binding):
 *   visit/travel segments → connected work-period COMPONENTS → min-payment
 *   coverage → remaining unpaid gaps → broken-shift count.
 * Group first within a component; anchor once per component.
 *
 * Derivation version 1.1.0 — work-period components + fail-closed rework
 * (adjudicated S16 audit fixes R1–R14).
 *
 * Derivation version 1.2.0 — R1c: cl 25.6(c) period formation. Visits whose
 * gap closes inside the running minimum-payment window are one period of work,
 * so the minimum payment lands per period rather than per visit.
 *
 * Derivation version 1.3.0 — R1c wholly-within correction (absorption and
 * formation now apply the same test) + R1d: broken-shift breaks counted at DAY
 * level, so the cl 20.12 allowance is payable once per shift and a roster with
 * more than two unpaid breaks hard-blocks (cl 25.6(a)(i)/(b)(i)).
 *
 * Derivation version 1.3.1 — the cl 20.12 allowance is carried by exactly one
 * anchor per day. 1.3.0 attributed the day total to the earliest anchor without
 * clearing per-component counts on the others, so a day with both a
 * between-period and an intra-component break paid two allowances for one
 * shift (found in external audit).
 *
 * Derivation version 1.4.0 — breaks and the cl 20.12 allowance are attributed
 * per SHIFT, not per calendar day: a gap of at least the cl 25.4(a) 10-hour
 * rest separates two shifts, so two lawful broken shifts on one date no longer
 * read as one unlawful 3-break day. A broken-shift override on a non-carrier
 * anchor now blocks for human resolution instead of emitting a second
 * allowance. Both found by external audit.
 *
 * Derivation version 1.5.0 — home-care award treatment is declared by the
 * service-catalogue `program` (SAH/CHSP in, RAC out). Free-text regex remains
 * a last-resort fallback only when no catalogue program is present.
 */

export const SAH_WAGE_CAPTURE_DERIVATION_VERSION = '1.5.1';

/** Contiguity tolerance between travel end and destination visit start. */
export const TRAVEL_ADJACENCY_TOLERANCE_MS = 2 * 60 * 1000;

/** Merge tolerance for adjacent paid blocks (1 minute). */
export const SEGMENT_MERGE_TOLERANCE_MS = 60 * 1000;

/**
 * MA000100 cl 25.4(a) — minimum 10-hour break between rostered work. A gap at
 * least this long separates two SHIFTS; anything shorter is a break within one
 * broken shift (cl 25.6).
 */
export const MINIMUM_REST_BETWEEN_SHIFTS_MS = 10 * 60 * 60 * 1000;

export type SahWageCaptureStatus =
  | 'PENDING'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'OPEN'
  | 'APPROVED'
  | 'PAID'
  | 'REJECTED'
  | string;

export type BrokenShiftSource = 'DERIVED' | 'OVERRIDE' | 'MANUAL';

export type TravelPayableBasis =
  | 'PROVIDER_POLICY'
  | 'EA'
  | 'CONTRACT'
  | 'FWO_BETWEEN_CLIENT'
  | 'UNKNOWN';

export type TravelCaptureSource =
  | 'SERVICES_DELIVERED'
  | 'ROSTER'
  | 'WORKER'
  | 'COORDINATOR'
  | 'DERIVED';

/** Open statuses that may receive automatic re-derivation writes. */
export const SAH_WAGE_CAPTURE_MUTABLE_STATUSES = new Set(['PENDING', 'DRAFT', 'SUBMITTED', 'OPEN']);

export const SAH_WAGE_CAPTURE_IMMUTABLE_STATUSES = new Set(['APPROVED', 'PAID']);

/** Capture sources trusted without an explicit approval stamp. */
const SYSTEM_TRUSTED_CAPTURE_SOURCES = new Set<TravelCaptureSource>([
  'SERVICES_DELIVERED',
  'ROSTER',
  'DERIVED',
]);

const AUTHORITATIVE_APPROVAL = new Set(['APPROVED', 'AUTHORITATIVE', 'CONFIRMED', 'ACCEPTED']);

const REJECTED_APPROVAL = new Set(['REJECTED', 'DENIED', 'UNAPPROVED', 'DECLINED']);

export interface SahVisitSegment {
  timesheetId: string;
  staffId: string;
  /** Tenant-local calendar date YYYY-MM-DD. */
  localDate: string;
  visitStart: Date | string;
  visitEnd: Date | string;
  status: SahWageCaptureStatus;
  /**
   * Unpaid meal/rest break minutes on this visit (timesheets.break_minutes).
   * Subtracted from paid minutes; does NOT create a broken-shift gap by itself.
   */
  breakMinutes?: number | null;
  /** Optional explicit unpaid break minutes; defaults to breakMinutes. */
  unpaidBreakMinutes?: number | null;
  /**
   * Meal-break minutes already counted in breakMinutes. Distinct from
   * inter-visit broken-shift gaps (cl 25.6).
   */
  mealBreakMinutes?: number | null;
  /** Existing override (if any). Re-derivation never clears this. */
  brokenShiftOverride?: 0 | 1 | 2 | null;
  brokenShiftOverrideReason?: string | null;
  travelKmExisting?: number | null;
  /** When set, links visits into the same assignment/roster component. */
  connectionGroupId?: string | null;
  /** pay_run_id present or APPROVED/PAID → immutable. */
  payRunId?: string | null;
  /**
   * SAH / home-care service-delivery eligibility. When false, the visit is
   * excluded from S16 derivation (legacy path only).
   */
  sahEligible?: boolean;
  /**
   * Frozen work-period facts from a locked sibling (mixed locked/open day).
   * When present on an immutable visit, mutable siblings in the same component
   * must reuse this component's locked snapshot rather than re-derive.
   */
  lockedWorkPeriodSnapshot?: SahTimesheetWageCaptureFacts | null;
}

export interface SahTravelSegment {
  id?: string;
  fromTimesheetId?: string | null;
  toTimesheetId: string;
  startTime: Date | string;
  endTime: Date | string;
  distanceKm?: number | null;
  /** When true, travel is treated as worked time on the timeline. */
  payable: boolean;
  payableBasis?: TravelPayableBasis;
  captureSource?: TravelCaptureSource;
  approvalState?: string | null;
}

export interface SahMinEngagementConfig {
  minimumEngagementMinutes: number | null | undefined;
  /**
   * Verified clause reference. Required when minutes > 0 — fail closed
   * if missing (blocking config error; never invent a legal clause).
   */
  minimumEngagementClauseRef: string | null | undefined;
  employmentType?: string | null;
  awardCode?: string | null;
  /**
   * When true, apply SCHADS cl 10.5(b) defaults for PT/casual if profile
   * minutes are null. Full-time still has no Award per-shift floor.
   */
  applySchadsHomeCareDefault?: boolean;
}

export interface SahDispatchSegment {
  kind: 'visit' | 'travel' | 'combined';
  startTime: string;
  endTime: string;
  breakMinutes: number;
  travelKm: number;
}

export interface SahTimesheetWageCaptureFacts {
  timesheetId: string;
  workPeriodId: string;
  isAnchor: boolean;
  /** Resolved 0|1|2 for engine input (override ?? derived). Null when blocked. */
  brokenShiftCount: 0 | 1 | 2 | null;
  brokenShiftDerived: 0 | 1 | 2 | null;
  brokenShiftSource: BrokenShiftSource;
  brokenShiftBlocked: boolean;
  brokenShiftBlockReason: string | null;
  /** Kilometres allocated to this timesheet (destination). */
  travelKm: number;
  /**
   * Contiguous engine window for this timesheet only when travel is adjacent.
   * Never bridges non-contiguous travel into one continuous window.
   */
  engineStartTime: string;
  engineEndTime: string;
  /**
   * Ordered contiguous paid segments for multi-window dispatch when travel is
   * separated (non-adjacent). Callers must not invent a bridged window.
   */
  dispatchSegments: SahDispatchSegment[];
  payableTravelMinutes: number;
  inboundTravel: {
    startTime: string;
    endTime: string;
    distanceKm: number;
    payableBasis: TravelPayableBasis;
    captureSource: TravelCaptureSource;
    contiguous: boolean;
  } | null;
  /** Work-period paid minutes (visits − unpaid breaks + payable contiguous travel). */
  workPeriodPaidMinutes: number;
  workPeriodAbsorbedGapMinutes: number;
  workPeriodTopUpMinutes: number;
  /** Minutes to pass into automatic top-up on the anchor only. */
  minimumEngagementMinutes: number | null;
  minimumEngagementClauseRef: string | null;
  suppressMinimumEngagementTopUp: boolean;
  remainingUnpaidGaps: number;
  derivationVersion: string;
  /** Evidence of excluded / rejected / non-contiguous segments. */
  evidence: string[];
  /** Hard regulated block — no pay until correction/override. */
  hardBlocked: boolean;
  hardBlockReasons: string[];
}

export interface SahWageCaptureWorkPeriodResult {
  workPeriodId: string;
  blocked: boolean;
  blockReason: string | null;
  floorMinutes: number;
  floorClauseRef: string | null;
  /** Blocking config error when floor > 0 without verified clause. */
  floorFailClosed: string | null;
  paidMinutes: number;
  absorbedGapMinutes: number;
  topUpMinutes: number;
  remainingUnpaidGaps: number;
  brokenShiftDerived: 0 | 1 | 2 | null;
  anchorTimesheetId: string | null;
  timesheetIds: string[];
  frozenFromLockedSnapshot: boolean;
  evidence: string[];
}

export interface SahWageCaptureDayResult {
  derivationVersion: string;
  staffId: string;
  localDate: string;
  workPeriods: SahWageCaptureWorkPeriodResult[];
  blocked: boolean;
  blockReasons: string[];
  /** Flattened per-timesheet facts (all components). */
  timesheets: SahTimesheetWageCaptureFacts[];
  helperText: string[];
  /** Segments excluded from derivation (approval / conflict / adjacency). */
  excludedEvidence: string[];
  /**
   * Mixed locked+open component handling: when true, mutable rows must not
   * re-derive across the lock boundary without a correction workflow.
   */
  mixedLockFreeze: boolean;
}

/** Shared versioned dispatch input — forecast ≡ recompute ≡ snapshot. */
export interface SahWageCaptureDispatchInput {
  derivationVersion: string;
  timesheetId: string;
  workPeriodId: string;
  isAnchor: boolean;
  /** Contiguous windows to dispatch (never a bridged non-contiguous window). */
  segments: SahDispatchSegment[];
  hasBrokenShift: 0 | 1 | 2;
  travelKm: number;
  minimumEngagementMinutes: number | null;
  minimumEngagementClauseRef: string | null;
  suppressMinimumEngagementTopUp: boolean;
  /** Work-period paid hours for period-aware top-up (anchor only). */
  minimumEngagementHoursWorkedOverride: number | null;
  hardBlocked: boolean;
  hardBlockReasons: string[];
}

function parseMs(value: Date | string | null | undefined): number | null {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function positiveMinutes(startMs: number, endMs: number): number {
  if (endMs <= startMs) return 0;
  return (endMs - startMs) / (60 * 1000);
}

function withinTolerance(a: number, b: number, tolMs: number): boolean {
  return Math.abs(a - b) <= tolMs;
}

export function isSahWageCaptureMutableStatus(status: string | null | undefined): boolean {
  return SAH_WAGE_CAPTURE_MUTABLE_STATUSES.has((status ?? '').toUpperCase());
}

export function isSahWageCaptureImmutableStatus(status: string | null | undefined): boolean {
  return SAH_WAGE_CAPTURE_IMMUTABLE_STATUSES.has((status ?? '').toUpperCase());
}

export function isVisitImmutable(visit: Pick<SahVisitSegment, 'status' | 'payRunId'>): boolean {
  if (visit.payRunId) return true;
  return isSahWageCaptureImmutableStatus(visit.status);
}

/**
 * R4 — only authoritative/approved travel segments enter derivation.
 * System-trusted capture sources may omit approval; worker/coordinator claims
 * require APPROVED/AUTHORITATIVE/CONFIRMED. Rejected always excluded.
 */
export function isTravelSegmentAuthoritative(seg: SahTravelSegment): {
  ok: boolean;
  reason?: string;
} {
  const state = (seg.approvalState ?? '').trim().toUpperCase();
  const source = (seg.captureSource ?? 'DERIVED') as TravelCaptureSource;

  if (REJECTED_APPROVAL.has(state)) {
    return { ok: false, reason: `travel rejected (${state}) to=${seg.toTimesheetId}` };
  }

  if (SYSTEM_TRUSTED_CAPTURE_SOURCES.has(source)) {
    if (state === '' || AUTHORITATIVE_APPROVAL.has(state) || state === 'PENDING') {
      // System sources: PENDING is treated as provisional OK only when no
      // conflicting approved claim exists (conflict check is separate).
      if (state === 'PENDING' && source === 'DERIVED') {
        return { ok: true };
      }
      if (state === 'PENDING') {
        return {
          ok: false,
          reason: `travel unapproved (${source}/${state || 'none'}) to=${seg.toTimesheetId}`,
        };
      }
      return { ok: true };
    }
    return {
      ok: false,
      reason: `travel not authoritative (${source}/${state}) to=${seg.toTimesheetId}`,
    };
  }

  // WORKER / COORDINATOR claims require explicit approval.
  if (AUTHORITATIVE_APPROVAL.has(state)) return { ok: true };
  return {
    ok: false,
    reason: `travel claim not approved (${source}/${state || 'none'}) to=${seg.toTimesheetId}`,
  };
}

/**
 * Resolve effective minimum-engagement floor.
 * R6: positive configured floor without verified clause ref = BLOCKING config
 * error (minutes retained for remediation messaging — never silent null floor).
 */
export function resolveMinimumEngagementFloor(config: SahMinEngagementConfig): {
  minutes: number;
  clauseRef: string | null;
  failClosed: string | null;
} {
  const employment = (config.employmentType ?? '').toUpperCase();
  const award = (config.awardCode ?? '').toUpperCase();
  let minutes =
    typeof config.minimumEngagementMinutes === 'number' &&
    Number.isFinite(config.minimumEngagementMinutes) &&
    config.minimumEngagementMinutes > 0
      ? Math.floor(config.minimumEngagementMinutes)
      : 0;
  let clauseRef =
    typeof config.minimumEngagementClauseRef === 'string' &&
    config.minimumEngagementClauseRef.trim().length > 0
      ? config.minimumEngagementClauseRef.trim()
      : null;

  if (
    minutes <= 0 &&
    config.applySchadsHomeCareDefault &&
    award === 'MA000100' &&
    employment !== 'FULL_TIME' &&
    (employment === 'CASUAL' ||
      employment === 'PART_TIME' ||
      employment === 'PART-TIME' ||
      employment === 'PERMANENT' ||
      employment === '')
  ) {
    minutes = 120;
    clauseRef = clauseRef ?? 'MA000100 cl 10.5(b)';
  }

  if (minutes > 0 && !clauseRef) {
    return {
      minutes,
      clauseRef: null,
      failClosed:
        'BLOCKING config: minimum_engagement_clause_ref required when minimum_engagement_minutes > 0. Seed a verified clause (e.g. MA000100 cl 10.5(b) or EA clause) on pay_ordinary_hours_profiles before payroll.',
    };
  }

  if (
    award === 'MA000100' &&
    employment === 'FULL_TIME' &&
    clauseRef &&
    /cl\s*10\.5/i.test(clauseRef)
  ) {
    return { minutes: 0, clauseRef: null, failClosed: null };
  }

  return { minutes, clauseRef, failClosed: null };
}

/** Effective broken-shift count for engine input. */
export function resolveBrokenShiftCount(input: {
  derived: 0 | 1 | 2 | null;
  override?: 0 | 1 | 2 | null;
}): { count: 0 | 1 | 2 | null; source: BrokenShiftSource } {
  if (input.override === 0 || input.override === 1 || input.override === 2) {
    return { count: input.override, source: 'OVERRIDE' };
  }
  if (input.derived === 0 || input.derived === 1 || input.derived === 2) {
    return { count: input.derived, source: 'DERIVED' };
  }
  return { count: null, source: 'DERIVED' };
}

/**
 * Cohort-aware helper text for coordinator UI (clause-specific).
 */
export function sahWageCaptureHelperText(args: {
  floorMinutes: number;
  floorClauseRef: string | null;
  absorbedGapMinutes: number;
  remainingUnpaidGaps: number;
  brokenShiftDerived: 0 | 1 | 2 | null;
  blocked: boolean;
  travelTimeBasis?: TravelPayableBasis | null;
  floorFailClosed?: string | null;
}): string[] {
  const lines: string[] = [];
  if (args.floorFailClosed) {
    lines.push(args.floorFailClosed);
  }
  if (args.floorMinutes > 0 && args.floorClauseRef) {
    lines.push(
      `Minimum payment: ${(args.floorMinutes / 60).toFixed(args.floorMinutes % 60 === 0 ? 0 : 2)}h per work period (${args.floorClauseRef}). Applied once per connected work-period component, not per client visit.`,
    );
  }
  if (args.absorbedGapMinutes > 0) {
    lines.push(
      `A break of ${Math.round(args.absorbedGapMinutes)} min falls within the minimum-payment period and is counted as time worked — it does not constitute a broken-shift break (MA000100 cl 25.6(c)).`,
    );
  }
  if (args.brokenShiftDerived === 1) {
    lines.push('Broken shift with 1 unpaid break (cl 25.6(a)) — allowance cl 20.12(a).');
  }
  if (args.brokenShiftDerived === 2) {
    lines.push(
      'Broken shift with 2 unpaid breaks requires prior agreement (cl 25.6(b)) — allowance cl 20.12(b). UI label is “2 breaks”, not “2 or more”.',
    );
  }
  if (args.blocked) {
    lines.push(
      'More than 2 unpaid breaks remaining after minimum-payment coverage — manual review required. Do not encode a fourth work period (cl 25.6). HARD BLOCK: no pay until approved correction/override.',
    );
  }
  if (args.travelTimeBasis) {
    if (args.travelTimeBasis === 'FWO_BETWEEN_CLIENT') {
      lines.push(
        'Between-client travel treated as worked time per provider configuration (FWO guidance). Vehicle kilometres reimbursed under cl 20.7(a); travel time is not itself a cl 20.7(a) entitlement.',
      );
    } else {
      lines.push(
        `Travel time payable basis: ${args.travelTimeBasis.replace(/_/g, ' ').toLowerCase()}. Vehicle km: cl 20.7(a).`,
      );
    }
  }
  return lines;
}

/**
 * Declared service-catalogue `program` → home-care award treatment.
 * SAH / CHSP are in. RAC is out. NDIS / PRIVATE / unknown are not a
 * declaration either way (caller may still use explicitSahEligible).
 */
export function catalogueProgramDeclaresHomeCareAward(
  program: string | null | undefined,
): boolean | null {
  if (typeof program !== 'string' || program.trim() === '') return null;
  const normalised = program.trim().toUpperCase();
  if (normalised === 'SAH' || normalised === 'CHSP') return true;
  if (normalised === 'RAC') return false;
  return null;
}

/**
 * R8 helper — SAH / home-care service-delivery cohort predicate.
 * Non-SAH timesheets never enter S16 derivation.
 *
 * Precedence: explicit flag, then service-catalogue `program`, then
 * residential exclusions, then last-resort free-text signals. A catalogue
 * RAC row must not be pulled back in by a PERSONAL_CARE regex.
 */
export function isSahHomeCareServiceDeliveryCohort(input: {
  serviceType?: string | null;
  serviceCode?: string | null;
  programType?: string | null;
  facilityType?: string | null;
  hasClientVisit?: boolean | null;
  wingId?: string | null;
  explicitSahEligible?: boolean | null;
  /** service_catalogues.program for the timesheet's service_code. */
  catalogueProgram?: string | null;
}): boolean {
  if (input.explicitSahEligible === true) return true;
  if (input.explicitSahEligible === false) return false;

  const declared = catalogueProgramDeclaresHomeCareAward(input.catalogueProgram);
  if (declared === false) return false;

  const fac = (input.facilityType ?? '').toUpperCase();
  // Residential / wing work is never SAH wage-capture — even if the catalogue
  // program is SAH/CHSP (mis-linked shift). RAC catalogue already returned above.
  if (input.wingId) return false;
  if (fac.includes('RESIDENTIAL') && !fac.includes('HOME_CARE')) return false;

  if (declared === true) return true;

  const st = (input.serviceType ?? '').toUpperCase();
  const sc = (input.serviceCode ?? '').toUpperCase();
  const prog = (input.programType ?? '').toUpperCase();

  if (
    prog.includes('SAH') ||
    prog.includes('SUPPORT_AT_HOME') ||
    prog.includes('HOME_CARE') ||
    prog.includes('HCP')
  ) {
    return true;
  }

  if (
    /PERSONAL_CARE|DOMESTIC|HOME_CARE|SOCIAL_SUPPORT|CLINICAL_CARE|NURSING|ALLIED|TRANSPORT|RESPITE|MEAL|GARDEN|HOME_MAINTENANCE|COMMUNITY|SAH/i.test(
      st,
    ) ||
    /PERSONAL_CARE|DOMESTIC|HOME_CARE|SAH|HCP|COMMUNITY/i.test(sc)
  ) {
    return true;
  }

  if (input.hasClientVisit && !input.wingId && (fac.includes('HOME_CARE') || fac === '')) {
    return true;
  }

  return false;
}

/** Stable work-period id from component membership — never staffId:localDate alone. */
export function buildWorkPeriodId(input: {
  staffId: string;
  localDate: string;
  timesheetIds: string[];
}): string {
  const sorted = [...input.timesheetIds].sort();
  const anchor = sorted[0] ?? 'empty';
  // Include all member ids (capped) so components never collide on staff:date.
  const members = sorted.join('+');
  const compact = members.length > 180 ? `${anchor}+n${sorted.length}` : members;
  return `wp:${input.staffId.slice(0, 8)}:${input.localDate}:${compact}`;
}

interface ResolvedVisit {
  visit: SahVisitSegment;
  startMs: number;
  endMs: number;
  unpaidBreakMinutes: number;
  paidVisitMinutes: number;
}

interface ResolvedTravel {
  seg: SahTravelSegment;
  startMs: number;
  endMs: number;
  distanceKm: number;
  contiguousWithVisit: boolean;
  toTimesheetId: string;
}

function unpaidBreakForVisit(v: SahVisitSegment): number {
  const raw =
    typeof v.unpaidBreakMinutes === 'number' && Number.isFinite(v.unpaidBreakMinutes)
      ? v.unpaidBreakMinutes
      : typeof v.breakMinutes === 'number' && Number.isFinite(v.breakMinutes)
        ? v.breakMinutes
        : 0;
  return Math.max(0, Math.floor(raw));
}

/**
 * R9 — ONE shared versioned dispatch-input builder from derived facts.
 * Stale/missing derivation version is a reject (caller must not legacy-fallback).
 */
export function buildSahWageCaptureDispatchInput(
  facts: SahTimesheetWageCaptureFacts,
  options?: { expectedDerivationVersion?: string },
): { ok: true; input: SahWageCaptureDispatchInput } | { ok: false; error: string } {
  const expected = options?.expectedDerivationVersion ?? SAH_WAGE_CAPTURE_DERIVATION_VERSION;
  if (!facts.derivationVersion) {
    return {
      ok: false,
      error: 'wage_capture derivation version missing — refuse legacy fallback',
    };
  }
  if (facts.derivationVersion !== expected) {
    return {
      ok: false,
      error: `wage_capture derivation version stale (${facts.derivationVersion} ≠ ${expected}) — re-derive before pay`,
    };
  }
  if (facts.hardBlocked) {
    return {
      ok: false,
      error: facts.hardBlockReasons.join('; ') || 'wage_capture hard blocked',
    };
  }
  if (facts.brokenShiftBlocked && facts.brokenShiftSource !== 'OVERRIDE') {
    return {
      ok: false,
      error:
        facts.brokenShiftBlockReason ??
        'broken shift blocked (>2 unpaid breaks) — no pay until approved correction',
    };
  }
  if (facts.brokenShiftCount === null) {
    return {
      ok: false,
      error: 'broken_shift_count unresolved — no pay until approved correction/override',
    };
  }

  const segments =
    facts.dispatchSegments.length > 0
      ? facts.dispatchSegments
      : [
          {
            kind: 'combined' as const,
            startTime: facts.engineStartTime,
            endTime: facts.engineEndTime,
            breakMinutes: 0,
            travelKm: facts.travelKm,
          },
        ];

  return {
    ok: true,
    input: {
      derivationVersion: facts.derivationVersion,
      timesheetId: facts.timesheetId,
      workPeriodId: facts.workPeriodId,
      isAnchor: facts.isAnchor,
      segments,
      hasBrokenShift: facts.brokenShiftCount,
      travelKm: facts.travelKm,
      minimumEngagementMinutes: facts.suppressMinimumEngagementTopUp
        ? null
        : facts.minimumEngagementMinutes,
      minimumEngagementClauseRef: facts.suppressMinimumEngagementTopUp
        ? null
        : facts.minimumEngagementClauseRef,
      suppressMinimumEngagementTopUp: facts.suppressMinimumEngagementTopUp,
      minimumEngagementHoursWorkedOverride:
        facts.isAnchor && facts.workPeriodPaidMinutes > 0 ? facts.workPeriodPaidMinutes / 60 : null,
      hardBlocked: facts.hardBlocked,
      hardBlockReasons: facts.hardBlockReasons,
    },
  };
}

/**
 * Top-up input shared by forecast / recompute / snapshot from dispatch plan.
 */
export function topUpsFromDispatchInput(
  dispatch: SahWageCaptureDispatchInput,
  base: { awardCode: string; employmentType: string },
): {
  awardCode: string;
  employmentType: string;
  minimumEngagementMinutes: number | null;
  minimumEngagementClauseRef: string | null;
  minimumEngagementComponentCode: string | null;
  suppressMinimumEngagementTopUp: boolean;
  minimumEngagementHoursWorkedOverride: number | null;
} {
  return {
    awardCode: base.awardCode,
    employmentType: base.employmentType,
    minimumEngagementMinutes: dispatch.minimumEngagementMinutes,
    minimumEngagementClauseRef: dispatch.minimumEngagementClauseRef,
    minimumEngagementComponentCode: dispatch.minimumEngagementMinutes
      ? 'PROFILE_MINIMUM_ENGAGEMENT_TOP_UP'
      : null,
    suppressMinimumEngagementTopUp: dispatch.suppressMinimumEngagementTopUp,
    minimumEngagementHoursWorkedOverride: dispatch.minimumEngagementHoursWorkedOverride,
  };
}

/**
 * Derive SAH wage-capture facts for one worker local day.
 * Pure and idempotent: same inputs → same outputs (stable sort).
 *
 * R1: connected work-period COMPONENTS (travel/adjacency/connectionGroup).
 * R2: travel adjacency validated — never bridge non-contiguous windows.
 * R3: unpaid break_minutes subtracted from paid minutes.
 * R4: only authoritative travel segments.
 */
export function deriveSahWageCaptureDay(input: {
  staffId: string;
  localDate: string;
  visits: SahVisitSegment[];
  travelSegments: SahTravelSegment[];
  minEngagement: SahMinEngagementConfig;
}): SahWageCaptureDayResult {
  const derivationVersion = SAH_WAGE_CAPTURE_DERIVATION_VERSION;
  const floor = resolveMinimumEngagementFloor(input.minEngagement);
  const excludedEvidence: string[] = [];
  const dayBlockReasons: string[] = [];

  if (floor.failClosed) {
    dayBlockReasons.push(floor.failClosed);
  }

  // R8 — only SAH-eligible visits (explicit false excluded; undefined = treat as eligible
  // when caller already filtered the day query).
  const visitsRaw = [...input.visits]
    .filter((v) => v.staffId === input.staffId && v.localDate === input.localDate)
    .filter((v) => v.sahEligible !== false);

  const visits: ResolvedVisit[] = visitsRaw
    .map((v) => {
      const startMs = parseMs(v.visitStart);
      const endMs = parseMs(v.visitEnd);
      if (startMs === null || endMs === null || endMs <= startMs) return null;
      const unpaidBreakMinutes = unpaidBreakForVisit(v);
      const span = positiveMinutes(startMs, endMs);
      const paidVisitMinutes = Math.max(0, span - unpaidBreakMinutes);
      return { visit: v, startMs, endMs, unpaidBreakMinutes, paidVisitMinutes };
    })
    .filter((v): v is ResolvedVisit => v !== null)
    .sort(
      (a, b) => a.startMs - b.startMs || a.visit.timesheetId.localeCompare(b.visit.timesheetId),
    );

  if (visits.length === 0) {
    return {
      derivationVersion,
      staffId: input.staffId,
      localDate: input.localDate,
      workPeriods: [],
      blocked: dayBlockReasons.length > 0,
      blockReasons: dayBlockReasons,
      timesheets: [],
      helperText: floor.failClosed ? [floor.failClosed] : [],
      excludedEvidence,
      mixedLockFreeze: false,
    };
  }

  // R4 — filter travel by approval; detect conflicts.
  const byTo = new Map<string, ResolvedTravel[]>();

  for (const t of input.travelSegments) {
    const auth = isTravelSegmentAuthoritative(t);
    if (!auth.ok) {
      excludedEvidence.push(auth.reason ?? 'travel excluded');
      continue;
    }
    const startMs = parseMs(t.startTime);
    const endMs = parseMs(t.endTime);
    if (startMs === null || endMs === null || endMs <= startMs) {
      excludedEvidence.push(`travel invalid window to=${t.toTimesheetId}`);
      continue;
    }
    if (!t.payable) {
      excludedEvidence.push(`travel non-payable to=${t.toTimesheetId}`);
      continue;
    }
    const resolved: ResolvedTravel = {
      seg: t,
      startMs,
      endMs,
      distanceKm: Number(t.distanceKm ?? 0) || 0,
      contiguousWithVisit: false,
      toTimesheetId: t.toTimesheetId,
    };
    const list = byTo.get(t.toTimesheetId) ?? [];
    list.push(resolved);
    byTo.set(t.toTimesheetId, list);
  }

  // Conflicting claims for same destination.
  for (const [toId, list] of byTo) {
    if (list.length <= 1) continue;
    const signatures = new Set(
      list.map((x) => `${x.startMs}|${x.endMs}|${x.distanceKm}|${x.seg.captureSource}`),
    );
    if (signatures.size > 1) {
      dayBlockReasons.push(`Conflicting travel claims for timesheet ${toId} — resolve before pay`);
      excludedEvidence.push(`conflict: multiple travel claims to=${toId}`);
      byTo.set(toId, []);
    }
  }

  // Attach best inbound travel per visit; validate adjacency (R2).
  const inboundByTimesheet = new Map<string, ResolvedTravel>();
  for (const rv of visits) {
    const candidates = (byTo.get(rv.visit.timesheetId) ?? []).sort((a, b) => a.startMs - b.startMs);
    if (candidates.length === 0) continue;
    // Prefer contiguous (end near visit start); else mark non-contiguous.
    const contiguous = candidates.find((c) =>
      withinTolerance(c.endMs, rv.startMs, TRAVEL_ADJACENCY_TOLERANCE_MS),
    );
    const chosen = contiguous ?? candidates[candidates.length - 1]!;
    chosen.contiguousWithVisit = Boolean(contiguous);
    if (!chosen.contiguousWithVisit) {
      excludedEvidence.push(
        `travel non-contiguous to=${rv.visit.timesheetId} (not bridged into engine window)`,
      );
      // R2: do not attach non-contiguous travel into continuous engine window.
      // Separated segment path: still record for multi-segment dispatch if payable.
      inboundByTimesheet.set(rv.visit.timesheetId, chosen);
    } else {
      inboundByTimesheet.set(rv.visit.timesheetId, chosen);
    }
  }

  // R1 — connected components via travel links / adjacency / connectionGroupId.
  const n = visits.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    let x = i;
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]!]!;
      x = parent[x]!;
    }
    return x;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  const indexById = new Map(visits.map((v, i) => [v.visit.timesheetId, i]));

  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const a = visits[i]!;
      const b = visits[j]!;
      const groupA = a.visit.connectionGroupId ?? null;
      const groupB = b.visit.connectionGroupId ?? null;
      if (groupA && groupB && groupA === groupB) {
        union(i, j);
        continue;
      }
      // Time-adjacent (≤ merge tolerance).
      if (b.startMs <= a.endMs + SEGMENT_MERGE_TOLERANCE_MS) {
        union(i, j);
        continue;
      }
      // Payable travel link from a → b (or any travel to b from a).
      const inboundB = inboundByTimesheet.get(b.visit.timesheetId);
      if (
        inboundB &&
        (inboundB.seg.fromTimesheetId === a.visit.timesheetId ||
          (inboundB.contiguousWithVisit &&
            withinTolerance(inboundB.startMs, a.endMs, TRAVEL_ADJACENCY_TOLERANCE_MS)))
      ) {
        union(i, j);
      }
    }
  }

  // Also connect via explicit from→to on any authoritative travel.
  for (const list of byTo.values()) {
    for (const t of list) {
      const toIdx = indexById.get(t.toTimesheetId);
      const fromIdx = t.seg.fromTimesheetId ? indexById.get(t.seg.fromTimesheetId) : undefined;
      if (toIdx !== undefined && fromIdx !== undefined) {
        union(fromIdx, toIdx);
      }
    }
  }

  // R1c — MA000100 cl 25.6(c): "Where a break in work falls within a minimum
  // payment period in accordance with clause 10.5 then it is to be counted as
  // time worked and does not constitute a break in a shift". [2021] FWCFB 2383
  // [491] confirms this covers inter-client travel and dead time.
  //
  // Gap absorption downstream only ever sees gaps INSIDE a component, so the
  // rule cannot fire unless the visits are in one component to begin with. The
  // other three linkers all depend on inputs a provider may simply not have —
  // connectionGroupId has no production writer, and a provider that records no
  // travel segments leaves only the 1-minute adjacency test. Without this pass
  // such a provider gets one component per visit, hence one minimum payment per
  // visit — which cl 25.6(a)(i)/(b)(i) does not permit at all, since a broken
  // shift is capped at 3 periods of work.
  //
  // Deliberately NOT a gap-duration threshold: the award sets no minimum time
  // between periods of work (FWO K600303), and position in the shift decides
  // the question — on a real roster a 16-minute gap can be a break while a
  // later 20-minute gap is time worked. So walk the day in start order and
  // measure each gap against the running period's minimum-payment window.
  if (floor.minutes > 0 && !floor.failClosed) {
    const floorMs = floor.minutes * 60 * 1000;
    const ordered = visits
      .map((v, i) => ({ idx: i, startMs: v.startMs, endMs: v.endMs }))
      .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
    let periodStartMs = ordered[0]?.startMs ?? 0;
    for (let k = 1; k < ordered.length; k += 1) {
      const prev = ordered[k - 1]!;
      const curr = ordered[k]!;
      if (curr.startMs < periodStartMs + floorMs) {
        // Break (if any) opens inside the minimum-payment period → time worked,
        // same period of work.
        union(prev.idx, curr.idx);
      } else {
        // Opens at or after the window → this closes the period and starts the
        // next period of work; downstream counts it as a broken-shift break.
        periodStartMs = curr.startMs;
      }
    }
  }

  const components = new Map<number, number[]>();
  for (let i = 0; i < n; i += 1) {
    const root = find(i);
    const list = components.get(root) ?? [];
    list.push(i);
    components.set(root, list);
  }

  // Order components by earliest visit start.
  const orderedComponents = [...components.values()]
    .map((idxs) => idxs.sort((a, b) => visits[a]!.startMs - visits[b]!.startMs))
    .sort((a, b) => visits[a[0]!]!.startMs - visits[b[0]!]!.startMs);

  const workPeriods: SahWageCaptureWorkPeriodResult[] = [];
  const allFacts: SahTimesheetWageCaptureFacts[] = [];
  let mixedLockFreeze = false;

  for (const idxs of orderedComponents) {
    const componentVisits = idxs.map((i) => visits[i]!);
    const timesheetIds = componentVisits.map((v) => v.visit.timesheetId);
    const workPeriodId = buildWorkPeriodId({
      staffId: input.staffId,
      localDate: input.localDate,
      timesheetIds,
    });

    // R12 — mixed locked/open: freeze or block re-derivation across the boundary.
    const immutable = componentVisits.filter((v) => isVisitImmutable(v.visit));
    const mutable = componentVisits.filter((v) => !isVisitImmutable(v.visit));
    const mixed = immutable.length > 0 && mutable.length > 0;

    if (mixed) {
      mixedLockFreeze = true;
      const lockedSnap =
        immutable.map((v) => v.visit.lockedWorkPeriodSnapshot).find((s) => s && s.workPeriodId) ??
        null;

      if (lockedSnap) {
        // Freeze: reuse locked work-period economics for all members; mutable
        // non-anchors keep suppress flags so they never silently lose anchor pay.
        const frozenWp: SahWageCaptureWorkPeriodResult = {
          workPeriodId: lockedSnap.workPeriodId,
          blocked: lockedSnap.hardBlocked || lockedSnap.brokenShiftBlocked,
          blockReason: lockedSnap.brokenShiftBlockReason,
          floorMinutes: lockedSnap.minimumEngagementMinutes ?? floor.minutes,
          floorClauseRef: lockedSnap.minimumEngagementClauseRef,
          floorFailClosed: floor.failClosed,
          paidMinutes: lockedSnap.workPeriodPaidMinutes,
          absorbedGapMinutes: lockedSnap.workPeriodAbsorbedGapMinutes,
          topUpMinutes: lockedSnap.workPeriodTopUpMinutes,
          remainingUnpaidGaps: lockedSnap.remainingUnpaidGaps,
          brokenShiftDerived: lockedSnap.brokenShiftDerived,
          anchorTimesheetId: componentVisits.find(
            (v) => v.visit.timesheetId === lockedSnap.timesheetId,
          )
            ? lockedSnap.timesheetId
            : componentVisits[0]!.visit.timesheetId,
          timesheetIds,
          frozenFromLockedSnapshot: true,
          evidence: [
            'mixed locked/open component — frozen from locked work-period snapshot',
            ...excludedEvidence.filter((e) => timesheetIds.some((id) => e.includes(id))),
          ],
        };
        workPeriods.push(frozenWp);

        for (const rv of componentVisits) {
          const isAnchor = rv.visit.timesheetId === frozenWp.anchorTimesheetId;
          const immutableVisit = isVisitImmutable(rv.visit);
          if (immutableVisit && rv.visit.lockedWorkPeriodSnapshot) {
            allFacts.push({
              ...rv.visit.lockedWorkPeriodSnapshot,
              workPeriodId: frozenWp.workPeriodId,
              derivationVersion,
            });
            continue;
          }
          // Mutable sibling: never silently become the anchor payment.
          allFacts.push({
            timesheetId: rv.visit.timesheetId,
            workPeriodId: frozenWp.workPeriodId,
            isAnchor: false,
            brokenShiftCount: 0,
            brokenShiftDerived: 0,
            brokenShiftSource: 'DERIVED',
            brokenShiftBlocked: false,
            brokenShiftBlockReason: null,
            travelKm: 0,
            engineStartTime: toIso(rv.startMs),
            engineEndTime: toIso(rv.endMs),
            dispatchSegments: [
              {
                kind: 'visit',
                startTime: toIso(rv.startMs),
                endTime: toIso(rv.endMs),
                breakMinutes: rv.unpaidBreakMinutes,
                travelKm: 0,
              },
            ],
            payableTravelMinutes: 0,
            inboundTravel: null,
            workPeriodPaidMinutes: frozenWp.paidMinutes,
            workPeriodAbsorbedGapMinutes: frozenWp.absorbedGapMinutes,
            workPeriodTopUpMinutes: frozenWp.topUpMinutes,
            minimumEngagementMinutes: null,
            minimumEngagementClauseRef: null,
            suppressMinimumEngagementTopUp: true,
            remainingUnpaidGaps: 0,
            derivationVersion,
            evidence: [
              'mixed lock freeze: mutable sibling suppressed — correction workflow required to re-anchor',
            ],
            hardBlocked: true,
            hardBlockReasons: [
              'Work period spans locked and open timesheets — correction workflow required; mutable row must not silently receive or lose anchor payment',
            ],
          });
          void isAnchor;
        }
        dayBlockReasons.push(
          `Work period ${workPeriodId} mixes locked and open timesheets — correction workflow required`,
        );
        continue;
      }

      // No locked snapshot available — hard block the component.
      dayBlockReasons.push(
        `Work period ${workPeriodId} mixes locked and open timesheets without a frozen snapshot — correction workflow required`,
      );
      for (const rv of componentVisits) {
        if (isVisitImmutable(rv.visit)) continue;
        allFacts.push({
          timesheetId: rv.visit.timesheetId,
          workPeriodId,
          isAnchor: false,
          brokenShiftCount: null,
          brokenShiftDerived: null,
          brokenShiftSource: 'DERIVED',
          brokenShiftBlocked: true,
          brokenShiftBlockReason: 'mixed locked/open component without frozen snapshot',
          travelKm: 0,
          engineStartTime: toIso(rv.startMs),
          engineEndTime: toIso(rv.endMs),
          dispatchSegments: [
            {
              kind: 'visit',
              startTime: toIso(rv.startMs),
              endTime: toIso(rv.endMs),
              breakMinutes: rv.unpaidBreakMinutes,
              travelKm: 0,
            },
          ],
          payableTravelMinutes: 0,
          inboundTravel: null,
          workPeriodPaidMinutes: 0,
          workPeriodAbsorbedGapMinutes: 0,
          workPeriodTopUpMinutes: 0,
          minimumEngagementMinutes: null,
          minimumEngagementClauseRef: null,
          suppressMinimumEngagementTopUp: true,
          remainingUnpaidGaps: 0,
          derivationVersion,
          evidence: ['mixed lock — no frozen snapshot'],
          hardBlocked: true,
          hardBlockReasons: [
            'Mixed locked/open work period without frozen snapshot — no pay until correction workflow',
          ],
        });
      }
      workPeriods.push({
        workPeriodId,
        blocked: true,
        blockReason: 'mixed locked/open without frozen snapshot',
        floorMinutes: floor.minutes,
        floorClauseRef: floor.clauseRef,
        floorFailClosed: floor.failClosed,
        paidMinutes: 0,
        absorbedGapMinutes: 0,
        topUpMinutes: 0,
        remainingUnpaidGaps: 0,
        brokenShiftDerived: null,
        anchorTimesheetId: componentVisits[0]!.visit.timesheetId,
        timesheetIds,
        frozenFromLockedSnapshot: false,
        evidence: ['mixed lock block'],
      });
      continue;
    }

    // R13 — conflicting overrides within a component.
    const overrideVisits = componentVisits.filter(
      (v) =>
        v.visit.brokenShiftOverride === 0 ||
        v.visit.brokenShiftOverride === 1 ||
        v.visit.brokenShiftOverride === 2,
    );
    const overrideValues = new Set(overrideVisits.map((v) => v.visit.brokenShiftOverride));
    let componentOverride: 0 | 1 | 2 | null = null;
    let overrideConflict: string | null = null;
    if (overrideValues.size > 1) {
      overrideConflict = `Conflicting broken-shift overrides in work period ${workPeriodId}`;
      dayBlockReasons.push(overrideConflict);
    } else if (overrideValues.size === 1) {
      componentOverride = [...overrideValues][0] as 0 | 1 | 2;
    }

    // Build paid segments: contiguous travel + visits (R2/R3).
    type PaidSeg = {
      kind: 'visit' | 'travel';
      timesheetId: string;
      startMs: number;
      endMs: number;
      paidMinutes: number;
      distanceKm: number;
    };
    const paidSegs: PaidSeg[] = [];

    for (const rv of componentVisits) {
      const inbound = inboundByTimesheet.get(rv.visit.timesheetId);
      if (inbound?.contiguousWithVisit) {
        paidSegs.push({
          kind: 'travel',
          timesheetId: rv.visit.timesheetId,
          startMs: inbound.startMs,
          endMs: inbound.endMs,
          paidMinutes: positiveMinutes(inbound.startMs, inbound.endMs),
          distanceKm: inbound.distanceKm,
        });
      } else if (inbound && !inbound.contiguousWithVisit) {
        // Separated paid travel segment — counted in period paid minutes but
        // NOT merged into the visit engine window (R2).
        paidSegs.push({
          kind: 'travel',
          timesheetId: rv.visit.timesheetId,
          startMs: inbound.startMs,
          endMs: inbound.endMs,
          paidMinutes: positiveMinutes(inbound.startMs, inbound.endMs),
          distanceKm: inbound.distanceKm,
        });
      }
      paidSegs.push({
        kind: 'visit',
        timesheetId: rv.visit.timesheetId,
        startMs: rv.startMs,
        endMs: rv.endMs,
        paidMinutes: rv.paidVisitMinutes,
        distanceKm: 0,
      });
    }

    paidSegs.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

    // Merge overlapping/adjacent for gap detection (only contiguous timeline).
    // Separated non-contiguous travel is still paid but creates a gap classification
    // only between visit blocks when travel does not bridge them.
    const visitBlocks = componentVisits.map((rv) => {
      const inbound = inboundByTimesheet.get(rv.visit.timesheetId);
      if (inbound?.contiguousWithVisit) {
        return {
          startMs: Math.min(inbound.startMs, rv.startMs),
          endMs: rv.endMs,
          timesheetId: rv.visit.timesheetId,
        };
      }
      return { startMs: rv.startMs, endMs: rv.endMs, timesheetId: rv.visit.timesheetId };
    });
    visitBlocks.sort((a, b) => a.startMs - b.startMs);

    const merged: Array<{ startMs: number; endMs: number }> = [];
    for (const blk of visitBlocks) {
      const last = merged[merged.length - 1];
      if (!last || blk.startMs > last.endMs + SEGMENT_MERGE_TOLERANCE_MS) {
        merged.push({ startMs: blk.startMs, endMs: blk.endMs });
      } else {
        last.endMs = Math.max(last.endMs, blk.endMs);
      }
    }

    // Paid minutes = sum of visit paid (less unpaid breaks) + contiguous/separated payable travel.
    let paidMinutes = 0;
    for (const seg of paidSegs) {
      paidMinutes += seg.paidMinutes;
    }
    // Deduplicate overlapping travel+visit double-count when contiguous:
    // visit paid is net of breaks; travel is separate — no double count of overlap
    // because travel ends at visit start when contiguous.
    paidMinutes = Math.round(paidMinutes * 1000) / 1000;

    // Unpaid gaps between merged paid blocks (inter-visit only — not meal breaks).
    const unpaidGaps: Array<{ startMs: number; endMs: number; minutes: number }> = [];
    for (let i = 1; i < merged.length; i += 1) {
      const prev = merged[i - 1]!;
      const curr = merged[i]!;
      if (curr.startMs > prev.endMs + SEGMENT_MERGE_TOLERANCE_MS) {
        const minutes = positiveMinutes(prev.endMs, curr.startMs);
        if (minutes > 0) {
          unpaidGaps.push({ startMs: prev.endMs, endMs: curr.startMs, minutes });
        }
      }
    }

    // Floor fail-closed: still compute structure but hard-block pay.
    // Same minutes either way — a fail-closed floor still defines the window;
    // what changes is that pay is hard-blocked and gaps are not absorbed.
    const effectiveFloorMinutes = floor.minutes;
    let absorbedGapMinutes = 0;
    let remainingUnpaidGaps = 0;
    // cl 25.6(c) is a POSITION test, not a quantity test: a break "falls within
    // a minimum payment period" when it OPENS inside that period, measured from
    // the start of this period of work — whether or not the period has already
    // exceeded the floor. The previous implementation absorbed gaps only as far
    // as a floor shortfall, so a gap inside the window of an already-long period
    // was mis-counted as a broken-shift break (and left unpaid) while the same
    // gap in a short period was absorbed. Position decides it for both.
    const periodStartMs = merged[0]?.startMs ?? 0;
    const minimumPaymentWindowEndMs = periodStartMs + effectiveFloorMinutes * 60 * 1000;
    for (const gap of unpaidGaps) {
      if (floor.failClosed) {
        // Do not absorb gaps under an unverified floor.
        remainingUnpaidGaps += 1;
        continue;
      }
      // WHOLLY-WITHIN, not commencement. A break is absorbed only when it both
      // opens and CLOSES inside the window — i.e. work resumes before the
      // window ends. gap.endMs is the next visit's start, so this is the same
      // test the R1c formation pass applies, which is the point: the two passes
      // must not implement different readings of one clause.
      //
      // Settled by the FWO's only straddling worked example (K600303, "Sadia"):
      // period opens 10:00 (window to 12:00), work ends 11:45, next period
      // 13:30. The break OPENS inside the window, yet FWO counts it as a break,
      // measures it 11:45→13:30 (1.75h, from actual end of work — so no
      // apportionment either), and pays the 2-break allowance. A commencement
      // test would absorb it, leave Sadia one break, and pay the wrong
      // allowance. FWCFB [2021] 2383 [490] is to the same purpose: cl 25.6(c)
      // exists to clarify how the minimum payment interacts with the limits on
      // permitted breaks, not to dissolve them — a commencement test would
      // absorb a 5-hour gap merely because it opened a minute early.
      //
      // Window is half-open [start, start+floor): a break beginning exactly at
      // the boundary is NOT absorbed (FWCFB [491] P2 and FWO Sadia P2 both).
      if (gap.endMs < minimumPaymentWindowEndMs) {
        absorbedGapMinutes += gap.minutes;
        continue;
      }
      remainingUnpaidGaps += 1;
    }
    // NOTE — absorbed minutes are deliberately NOT added to paidMinutes here.
    // The downstream top-up pays `floor − workPeriodPaidMinutes`, so folding
    // absorbed time into paidMinutes shrinks the top-up without anything else
    // paying those minutes: a 60-min/30-gap period would fall from the 2h floor
    // to 90 minutes. Absorption below is therefore a CLASSIFICATION rule (the
    // gap is not a broken-shift break) and pay is unchanged from 1.1.0.
    //
    // KNOWN RESIDUE (next slice): where a period already exceeds the floor, an
    // in-window gap is correctly no longer counted as a break, but the gap time
    // itself is still unpaid — cl 25.6(c) says it "is to be counted as time
    // worked". Paying it needs either an explicit worked-time line for the gap
    // or an extended engine window (which also picks up the right time-of-day
    // penalty); both are beyond a classification change and must not be faked
    // by inflating the minimum-payment measure.
    const topUpMinutes = floor.failClosed ? 0 : Math.max(0, effectiveFloorMinutes - paidMinutes);

    let brokenShiftDerived: 0 | 1 | 2 | null = 0;
    let blocked = false;
    let blockReason: string | null = null;
    if (overrideConflict) {
      blocked = true;
      blockReason = overrideConflict;
      brokenShiftDerived = null;
    } else if (remainingUnpaidGaps > 2) {
      brokenShiftDerived = null;
      blocked = true;
      blockReason = `Work period has ${remainingUnpaidGaps} unpaid breaks after minimum-payment coverage; SCHADS cl 25.6 permits at most 2 (with agreement). Manual review required.`;
    } else if (remainingUnpaidGaps === 2) {
      brokenShiftDerived = 2;
    } else if (remainingUnpaidGaps === 1) {
      brokenShiftDerived = 1;
    } else {
      brokenShiftDerived = 0;
    }

    if (floor.failClosed) {
      blocked = true;
      blockReason = blockReason ? `${blockReason}; ${floor.failClosed}` : floor.failClosed;
    }

    const anchorTimesheetId = componentVisits[0]!.visit.timesheetId;
    // Prefer override attached to any visit; migrate provenance to anchor (R13).
    const resolved = resolveBrokenShiftCount({
      derived: brokenShiftDerived,
      override: componentOverride,
    });

    workPeriods.push({
      workPeriodId,
      blocked,
      blockReason,
      floorMinutes: floor.minutes,
      floorClauseRef: floor.clauseRef,
      floorFailClosed: floor.failClosed,
      paidMinutes,
      absorbedGapMinutes,
      topUpMinutes,
      remainingUnpaidGaps,
      brokenShiftDerived,
      anchorTimesheetId,
      timesheetIds,
      frozenFromLockedSnapshot: false,
      evidence: excludedEvidence.filter((e) => timesheetIds.some((id) => e.includes(id))),
    });

    for (const rv of componentVisits) {
      const isAnchor = rv.visit.timesheetId === anchorTimesheetId;
      const inbound = inboundByTimesheet.get(rv.visit.timesheetId) ?? null;

      const brokenShiftCount: 0 | 1 | 2 | null = isAnchor
        ? blocked && resolved.source !== 'OVERRIDE'
          ? null
          : resolved.count
        : 0;

      const hardBlockReasons: string[] = [];
      if (isAnchor && blocked && resolved.source !== 'OVERRIDE') {
        hardBlockReasons.push(blockReason ?? 'work period blocked');
      }
      if (isAnchor && floor.failClosed) {
        hardBlockReasons.push(floor.failClosed);
      }
      if (overrideConflict && isAnchor) {
        hardBlockReasons.push(overrideConflict);
      }

      // Engine window: NEVER bridge non-contiguous travel (R2).
      const dispatchSegments: SahDispatchSegment[] = [];
      let engineStartMs = rv.startMs;
      let engineEndMs = rv.endMs;
      let payableTravelMinutes = 0;
      let travelKm = round2(Number(rv.visit.travelKmExisting ?? 0) || 0);

      if (inbound?.contiguousWithVisit) {
        engineStartMs = Math.min(inbound.startMs, rv.startMs);
        payableTravelMinutes = positiveMinutes(inbound.startMs, inbound.endMs);
        travelKm = round2(inbound.distanceKm);
        dispatchSegments.push({
          kind: 'combined',
          startTime: toIso(engineStartMs),
          endTime: toIso(engineEndMs),
          breakMinutes: rv.unpaidBreakMinutes,
          travelKm,
        });
      } else if (inbound && !inbound.contiguousWithVisit) {
        // Separated paid segments — two engine windows, never one bridged window.
        payableTravelMinutes = positiveMinutes(inbound.startMs, inbound.endMs);
        travelKm = round2(inbound.distanceKm);
        dispatchSegments.push({
          kind: 'travel',
          startTime: toIso(inbound.startMs),
          endTime: toIso(inbound.endMs),
          breakMinutes: 0,
          travelKm,
        });
        dispatchSegments.push({
          kind: 'visit',
          startTime: toIso(rv.startMs),
          endTime: toIso(rv.endMs),
          breakMinutes: rv.unpaidBreakMinutes,
          travelKm: 0,
        });
        // Primary engine window remains the visit only (callers use dispatchSegments).
        engineStartMs = rv.startMs;
        engineEndMs = rv.endMs;
      } else {
        dispatchSegments.push({
          kind: 'visit',
          startTime: toIso(engineStartMs),
          endTime: toIso(engineEndMs),
          breakMinutes: rv.unpaidBreakMinutes,
          travelKm,
        });
      }

      const evidence: string[] = [];
      if (rv.unpaidBreakMinutes > 0) {
        evidence.push(
          `unpaid break ${rv.unpaidBreakMinutes} min subtracted from visit paid minutes (meal/rest — not a cl 25.6 broken-shift gap)`,
        );
      }
      if (inbound && !inbound.contiguousWithVisit) {
        evidence.push('inbound travel non-contiguous — dispatched as separated paid segments');
      }
      if (isAnchor && componentOverride !== null) {
        evidence.push('broken-shift override applied at work-period scope (migrated to anchor)');
      }

      allFacts.push({
        timesheetId: rv.visit.timesheetId,
        workPeriodId,
        isAnchor,
        brokenShiftCount,
        brokenShiftDerived: isAnchor ? brokenShiftDerived : 0,
        brokenShiftSource: isAnchor ? resolved.source : 'DERIVED',
        brokenShiftBlocked: isAnchor && blocked && resolved.source !== 'OVERRIDE',
        brokenShiftBlockReason: isAnchor && blocked ? blockReason : null,
        travelKm,
        engineStartTime: toIso(engineStartMs),
        engineEndTime: toIso(engineEndMs),
        dispatchSegments,
        payableTravelMinutes: Math.round(payableTravelMinutes * 1000) / 1000,
        inboundTravel: inbound
          ? {
              startTime: toIso(inbound.startMs),
              endTime: toIso(inbound.endMs),
              distanceKm: round2(inbound.distanceKm),
              payableBasis: inbound.seg.payableBasis ?? 'PROVIDER_POLICY',
              captureSource: inbound.seg.captureSource ?? 'DERIVED',
              contiguous: inbound.contiguousWithVisit,
            }
          : null,
        workPeriodPaidMinutes: paidMinutes,
        workPeriodAbsorbedGapMinutes: absorbedGapMinutes,
        workPeriodTopUpMinutes: topUpMinutes,
        minimumEngagementMinutes: isAnchor && !floor.failClosed ? floor.minutes || null : null,
        minimumEngagementClauseRef: isAnchor && !floor.failClosed ? floor.clauseRef : null,
        // No profile minutes must not suppress the award floor (E3).
        // applyAutomaticAwardTopUps derives SCHADS 2h/3h when profile is absent.
        suppressMinimumEngagementTopUp: !isAnchor || Boolean(floor.failClosed),
        remainingUnpaidGaps: isAnchor ? remainingUnpaidGaps : 0,
        derivationVersion,
        evidence,
        hardBlocked: hardBlockReasons.length > 0,
        hardBlockReasons,
      });
    }
  }

  // R1d — DAY-LEVEL broken-shift breaks (cl 25.6(a)(i)/(b)(i), allowance
  // cl 20.12). A broken shift is a property of the SHIFT, not of one period of
  // work: its breaks are the gaps BETWEEN periods. Counting only gaps inside a
  // component missed every one of them, so the allowance could not fire for a
  // provider whose periods are separate components — which, after R1c, is every
  // provider (a component's members all start inside one minimum-payment
  // window, so surviving intra-component gaps only occur via connectionGroupId
  // or travel links spanning a window).
  //
  // The allowance is per broken shift, so it is attributed ONCE per day, to the
  // earliest anchor. Later anchors carry 0 so the engine cannot pay it twice.
  if (workPeriods.length > 0 && !floor.failClosed) {
    const periodSpans = workPeriods
      .map((wp) => {
        const members = allFacts.filter((f) => f.workPeriodId === wp.workPeriodId);
        const starts = members.map((m) => Date.parse(m.engineStartTime)).filter(Number.isFinite);
        const ends = members.map((m) => Date.parse(m.engineEndTime)).filter(Number.isFinite);
        return {
          workPeriodId: wp.workPeriodId,
          startMs: starts.length ? Math.min(...starts) : Number.NaN,
          endMs: ends.length ? Math.max(...ends) : Number.NaN,
        };
      })
      .filter((p) => Number.isFinite(p.startMs) && Number.isFinite(p.endMs))
      .sort((a, b) => a.startMs - b.startMs);

    // A calendar day is not necessarily ONE shift. cl 25.4(a) requires a
    // 10-hour break between rostered work, so a gap of at least that length is
    // the rest BETWEEN two shifts — not an unpaid break inside one broken
    // shift. Without this split, two lawful one-break broken shifts on the same
    // date read as a single 3-break shift, hard-block, and the second shift
    // loses its own cl 20.12 allowance.
    //
    // KNOWN LIMIT — cross-midnight: the derivation is partitioned by staff-day
    // at its input boundary, so a broken shift spanning midnight is derived as
    // two days and each half counts its own breaks. Fixing that needs the input
    // window to extend past midnight for span purposes while attribution stays
    // on the roster date.
    const shiftGroups: Array<typeof periodSpans> = [];
    for (const span of periodSpans) {
      const currentGroup = shiftGroups[shiftGroups.length - 1];
      const previous = currentGroup?.[currentGroup.length - 1];
      if (
        !currentGroup ||
        !previous ||
        span.startMs - previous.endMs >= MINIMUM_REST_BETWEEN_SHIFTS_MS
      ) {
        shiftGroups.push([span]);
        continue;
      }
      currentGroup.push(span);
    }

    for (const shiftSpans of shiftGroups) {
      let betweenPeriodBreaks = 0;
      for (let i = 1; i < shiftSpans.length; i += 1) {
        const prev = shiftSpans[i - 1]!;
        const curr = shiftSpans[i]!;
        if (curr.startMs > prev.endMs + SEGMENT_MERGE_TOLERANCE_MS) betweenPeriodBreaks += 1;
      }
      const intraPeriodBreaks = shiftSpans.reduce(
        (sum, span) =>
          sum +
          (workPeriods.find((wp) => wp.workPeriodId === span.workPeriodId)?.remainingUnpaidGaps ??
            0),
        0,
      );
      const shiftBreaks = betweenPeriodBreaks + intraPeriodBreaks;
      const anchors = shiftSpans
        .map((span) => allFacts.find((f) => f.workPeriodId === span.workPeriodId && f.isAnchor))
        .filter((f): f is SahTimesheetWageCaptureFacts => Boolean(f));
      const shiftFacts = allFacts.filter((f) =>
        shiftSpans.some((span) => span.workPeriodId === f.workPeriodId),
      );

      if (shiftBreaks > 2) {
        // cl 25.6(a)(i) permits 1 unpaid break; cl 25.6(b)(i) permits 2 by
        // written agreement. More than 2 is not a lawful roster at any price,
        // so refuse rather than silently pricing it.
        dayBlockReasons.push(
          `Shift has ${shiftBreaks} unpaid breaks; SCHADS cl 25.6 permits at most 2 (and 2 only by written agreement per cl 25.6(b)(ii)). The roster must change — manual review required.`,
        );
        for (const facts of shiftFacts) {
          facts.hardBlocked = true;
          facts.hardBlockReasons = [
            ...facts.hardBlockReasons,
            `shift has ${shiftBreaks} unpaid breaks (cl 25.6 permits at most 2)`,
          ];
          facts.brokenShiftCount = null;
          facts.brokenShiftDerived = null;
        }
        continue;
      }

      if (betweenPeriodBreaks === 0) continue;

      // ONE allowance per broken shift (cl 20.12 "per broken shift"). The
      // per-component pass already set a count on any anchor whose own
      // component held a surviving gap; leaving those while also attributing
      // the shift total emits TWO allowance lines for one shift, and production
      // pays each anchor timesheet independently.
      const carrier = anchors[0];
      const derived = Math.min(2, shiftBreaks) as 0 | 1 | 2;
      // An override on a NON-carrier cannot simply be preserved: it would emit
      // a second allowance ($21.81 + $28.87 = $50.68 for one shift), which no
      // reading of cl 20.12 supports. Nor may the engine silently discard a
      // provider's explicit decision. So it is an unresolved conflict and
      // blocks for human resolution.
      const conflictingOverride = anchors.find(
        (f) => f !== carrier && f.brokenShiftSource === 'OVERRIDE' && (f.brokenShiftCount ?? 0) > 0,
      );
      if (conflictingOverride) {
        dayBlockReasons.push(
          `Broken-shift override on ${conflictingOverride.timesheetId} conflicts with the derived broken shift: cl 20.12 pays one allowance per broken shift, and the engine will not choose between a provider override and its own derivation. Resolve the override, then recompute.`,
        );
        for (const facts of shiftFacts) {
          facts.hardBlocked = true;
          facts.hardBlockReasons = [
            ...facts.hardBlockReasons,
            'broken-shift override conflicts with derivation (cl 20.12 is once per shift)',
          ];
        }
        continue;
      }
      for (const facts of anchors) {
        // An override on the CARRIER is the provider speaking about the shift
        // that carries the allowance — respected, others still zeroed.
        if (facts === carrier && facts.brokenShiftSource === 'OVERRIDE') continue;
        if (facts === carrier) {
          facts.brokenShiftDerived = derived;
          facts.brokenShiftCount = derived;
          facts.evidence = [
            ...facts.evidence,
            `Broken shift: ${shiftBreaks} unpaid break(s) (${betweenPeriodBreaks} between periods of work, ${intraPeriodBreaks} within) — allowance payable per MA000100 cl 20.12(${shiftBreaks >= 2 ? 'b' : 'a'}); attributed once to this shift's first period.`,
          ];
        } else {
          facts.brokenShiftDerived = 0;
          facts.brokenShiftCount = 0;
          facts.evidence = [
            ...facts.evidence,
            'Broken-shift allowance is payable once per shift and is carried by this shift’s first period of work.',
          ];
        }
      }
    }
  }

  const travelBasis =
    [...inboundByTimesheet.values()][0]?.seg.payableBasis ??
    input.travelSegments[0]?.payableBasis ??
    null;

  const anyBlocked = workPeriods.some((wp) => wp.blocked) || dayBlockReasons.length > 0;
  const primaryWp = workPeriods[0];
  const helperText = sahWageCaptureHelperText({
    floorMinutes: floor.minutes,
    floorClauseRef: floor.clauseRef,
    absorbedGapMinutes: primaryWp?.absorbedGapMinutes ?? 0,
    remainingUnpaidGaps: workPeriods.reduce((s, wp) => s + wp.remainingUnpaidGaps, 0),
    brokenShiftDerived: primaryWp?.brokenShiftDerived ?? 0,
    blocked: anyBlocked,
    travelTimeBasis: travelBasis,
    floorFailClosed: floor.failClosed,
  });

  return {
    derivationVersion,
    staffId: input.staffId,
    localDate: input.localDate,
    workPeriods,
    blocked: anyBlocked,
    blockReasons: [...new Set(dayBlockReasons)],
    timesheets: allFacts,
    helperText,
    excludedEvidence,
    mixedLockFreeze,
  };
}

/**
 * Build a payable travel segment from destination visit start and a confirmed
 * duration (minutes). Uses actual end = visit start and start = end − duration
 * so penalty-band allocation uses real wall-clock timestamps.
 */
export function travelSegmentFromDuration(input: {
  toTimesheetId: string;
  fromTimesheetId?: string | null;
  visitStart: Date | string;
  durationMinutes: number;
  distanceKm?: number | null;
  payable?: boolean;
  payableBasis?: TravelPayableBasis;
  captureSource?: TravelCaptureSource;
  approvalState?: string | null;
}): SahTravelSegment | null {
  const endMs = parseMs(input.visitStart);
  if (endMs === null) return null;
  const minutes = Number(input.durationMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const startMs = endMs - minutes * 60 * 1000;
  return {
    fromTimesheetId: input.fromTimesheetId ?? null,
    toTimesheetId: input.toTimesheetId,
    startTime: toIso(startMs),
    endTime: toIso(endMs),
    distanceKm: input.distanceKm ?? 0,
    payable: input.payable !== false,
    payableBasis: input.payableBasis ?? 'PROVIDER_POLICY',
    captureSource: input.captureSource ?? 'SERVICES_DELIVERED',
    approvalState: input.approvalState ?? null,
  };
}

/**
 * Stable JSON snapshot of derivation outputs for forecast ≡ recompute ≡ snapshot.
 */
export function stableWageCaptureFactsJson(
  facts: SahTimesheetWageCaptureFacts | SahWageCaptureDayResult | SahWageCaptureDispatchInput,
): string {
  return JSON.stringify(facts, Object.keys(facts as object).sort());
}
